import React, { DragEventHandler, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { alpha, Box, Grid, IconButton, Menu, MenuItem, Stack, TableCell, TableRow as MuiTableRow, Typography } from '@mui/material'
import { blue, primary as primaryColor, red } from '../../assets/colors'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { ViewType } from '../../assets/constants'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import OrderBookDialog from '../orderBook/OrderBookDialog'
import { Security, SecurityCategory_CategoryCode } from '../../proto/generated/security'
import { Watchlist_WatchlistItem, Watchlist_WatchlistItem_Type } from '../../proto/generated/watchlist'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { exchangeToJSON } from '../../proto/generated/common'
import { ExtraSmallButtonHoverable, NonInteractiveTooltip } from '../custom'
import { selectCommonStore } from '../../selectors/common'
import { convertExpressionToDisplayExpression, getExpressionFromSaved } from '../../util/expression'
import { SecurityClickSource } from '../../events/eventTypings'
import BinIcon from '../../assets/icons/BinIcon'
import { Broker_OrderType, OrderType, Product, TransactionType } from '../../proto/generated/order'
import { setQuickOrderData } from '../../reducer/orders.reducer'
import { setAddRemoveWatchlistItem, setWatchlists, WatchlistItemData } from '../../reducer/watchList.reducer'
import { selectReferenceSecurityMap, selectUserSecurityExpressions } from '../../selectors/security'
import { handleOverviewSecurityClick } from '../securityInfo/SecurityOverviewHeader'
import { selectPrimarySecurity } from '../../selectors/home'
import store from '../../store'
import Chart from '../../assets/icons/Chart'
import { selectNumberOfCustomizedWatchlistColumns } from '../../selectors/user'
import SymbolDisplay from '../symbolDisplay/symbolDisplay'
import { generatePath, matchPath } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import OptionChain from '../../assets/icons/OptionChain'
import { changeWatchlistSecurities } from '../../services/apis/watchlist'
import { updateWatchlist } from './WatchUtils'
import { selectPreviewWatchlist, selectPreviewWatchlistId, selectWatchlists } from '../../selectors/watchList'
import PlusIcon from '../../assets/icons/PlusIcon'
import SecuritySearchModal from '../securitySearch/SecuritySearchModal'
import commonActions from '../../actions/common.actions'
import { ExpressionCallbackData } from '../securitySearch/typings'
import { parseErrorFromArrayBuffer } from '../common/errorParser'
import { setSecuritySearchOpen } from '../../reducer/home.reducer'
import AddSectionIcon from '../../assets/icons/addSectionIcon'
import { useShouldAllowAccess } from '../../hooks/useWatchlistAccess'
import SecurityRange from '../securityInfo/SecurityRange.tsx'
import { useAdminScreenerAccess } from '../../hooks/useAdminScreenerAccess'
import { FeatureCategory, FeatureName } from '../adminUserRestrictions/Restrictions.constants.ts'
import { setFeatureRestrictionDialog } from '../../reducer/user.reducer.ts'
import { useUser } from '../../context/UserContext.tsx'
import { WatchlistTableItemPrices } from './WatchlistItemPrices'
import { useWatchlistItemIsMatched } from './useWatchlistItemIsMatched'

interface WatchListItemProps {
    watchlistId: string
    addOrRemoveWatchlistDataItem: Function
    data: WatchlistItemData
    subscribeSecurity: Function
    handleDragOver: Function
    handleDragEnd: DragEventHandler<HTMLLIElement>
    handleDragStart: Function
    isDraggedOver: boolean
    index: number
    onItemClick: (e: React.MouseEvent, id: number) => void
    onItemActivate?: (item: WatchlistItemData) => void
    selectedItemIds: Set<string>
    showBottomDragLine?: boolean
    columnWidths?: Record<'symbol' | 'last' | 'change' | 'changePercentage' | 'volume', number>
    editMode: boolean
    onClearFocus?: () => void
}

export type WatchListDataForSort = {
    Security?: Security | undefined
    ltp: number
    ltpChange: number
    changePercentage: number
    volume: number
}
const WatchListTableView: React.FC<WatchListItemProps> = ({
    data,
    subscribeSecurity,
    handleDragOver,
    handleDragStart,
    handleDragEnd,
    isDraggedOver,
    addOrRemoveWatchlistDataItem,
    watchlistId,
    columnWidths,
    index,
    onItemClick,
    onItemActivate,
    selectedItemIds,
    showBottomDragLine,
    editMode,
    onClearFocus
}) => {
    const {
        item: { id: itemId, type, security, securityExpression }
    } = data
    const referenceSecurityMap = useSelector(selectReferenceSecurityMap)
    const dispatch = useDispatch()
    const previousDataRef = useRef<any | null>(null)
    const [expanded, setExpanded] = useState(false)
    const expressionMode = type === Watchlist_WatchlistItem_Type.EXPRESSION
    const addOrRemoveWatchlistDataItemData = useSelector((state: RootState) => state.watchListStore.addOrRemoveWatchlistItem)
    const [isHighlighted, setIsHighlighted] = useState(false)
    const initialSecurityRendered = useRef(false)
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [view, setView] = useState<ViewType>(ViewType.OPTION_CHAIN)
    const itemRef = useRef<HTMLTableRowElement>(null)
    const { watchlistId: selectedWatchlistId } = useSelector(selectCommonStore)
    const watchlists = useSelector(selectWatchlists)
    const previewId = useSelector(selectPreviewWatchlistId)
    const previewWatchlist = useSelector(selectPreviewWatchlist)
    const selectedWatchlist = useMemo(() => {
        if (previewId.isActive && previewWatchlist) {
            return previewWatchlist
        }
        return watchlists[watchlistId]
    }, [previewId.isActive, previewWatchlist, watchlists, watchlistId])
    const dataUpdating = useRef<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [modalOpen, setModalOpen] = useState(false)
    const [expressionNameCallbackData, setExpressionNameCallbackData] = useState<Watchlist_WatchlistItem | undefined>()
    const savedExpressions = useSelector(selectUserSecurityExpressions)
    const numberOfTableColumns = useSelector(selectNumberOfCustomizedWatchlistColumns)
    const shouldAllowAccess = useShouldAllowAccess()
    const shouldAllowAdminAccess = useAdminScreenerAccess()
    const {user: {hasAccess}} = useUser()
    const onSecuritySearchModalClose = useCallback(() => {
        setModalOpen(false)
    }, [])
    const rectCache = useRef<DOMRect | null>(null)
    const [contextMenu, setContextMenu] = useState<{
        mouseX: number
        mouseY: number
        index: number
    } | null>(null)
    const isRightClicked = contextMenu?.index === index

    const selectedItems = useMemo((): Set<string> => {
        const set = new Set<string>()
        if (!selectedWatchlist || !selectedWatchlist.watchlistItems) return set

        selectedWatchlist.watchlistItems.forEach((watchlistItem) => {
            const { type, security, securityExpression } = watchlistItem.item
            if (type === Watchlist_WatchlistItem_Type.SECURITY) {
                if (!security) {
                    return
                }
                set.add(getSecurityKey(security.exchange, security.code))
            } else if (type === Watchlist_WatchlistItem_Type.EXPRESSION) {
                if (!securityExpression) {
                    return
                }
                set.add(securityExpression.expression)
            }
        })
        return set
    }, [selectedWatchlist])

    const isMatched = useWatchlistItemIsMatched(data)

    const [selectedSecurity, setSelectedSecurity] = useState<Security | undefined>()
    const [referenceSecurity, setReferenceSecurity] = useState<Security | undefined>()
    useEffect(() => {
        if (!security) {
            setSelectedSecurity(undefined)
            setReferenceSecurity(undefined)
            return
        }
        const key = getSecurityKey(security.exchange, security.code)
        getSecurityAsync(key).then((sec) => {
            setSelectedSecurity(sec ?? undefined)
            if (!sec?.derived) {
                setReferenceSecurity(undefined)
                return
            }
            const refKey = referenceSecurityMap[key]
            getSecurityAsync(refKey).then(setReferenceSecurity)
        })
    }, [security, referenceSecurityMap])

    useEffect(() => {
        if (addOrRemoveWatchlistDataItemData?.showHighlighted && addOrRemoveWatchlistDataItemData.watchlistItem.id === itemId) {
            setIsHighlighted(true)
            const timer = setTimeout(() => {
                setIsHighlighted(false)
                dispatch(setAddRemoveWatchlistItem())
            }, 3000)

            return () => clearTimeout(timer)
        }
    }, [addOrRemoveWatchlistDataItemData, dispatch, itemId])

    const handleExpandClick = useCallback(() => {
        if (!expressionMode && security) {
            subscribeSecurity(security, !expanded)
            setExpanded(!expanded)
        }
    }, [expanded, expressionMode, subscribeSecurity, security])

    const securitiesCode = useMemo(() => (security ? [{ exchange: security.exchange, code: security.code }] : []), [security])

    useEffect(() => {
        if (initialSecurityRendered.current) return
        const { key, expressionMode: currentPrimaryExpressionMode, expressionSecurities } =
            selectPrimarySecurity(store.getState())
        if (!key) return
        if (!currentPrimaryExpressionMode) {
            previousDataRef.current = {
                key: key,
                securities: securitiesCode.map((v) => getSecurityKey(v.exchange, v.code)),
                expressionMode: false,
                source: SecurityClickSource.WATCHLIST
            }
            initialSecurityRendered.current = true
        } else {
            previousDataRef.current = {
                key: key,
                securities: expressionSecurities.map((v) => getSecurityKey(v.exchange, v.code)),
                expressionMode: true,
                source: SecurityClickSource.WATCHLIST
            }
        }
    }, [securitiesCode])



    const activateItem = useCallback(() => {
        onItemActivate?.(data)
    }, [data, onItemActivate])

    const onAddSymbolSectionOrEnd = useCallback(async () => {
        setModalOpen(true)
    }, [])

    const getPathForOptionChain = useCallback((): string => {
        return generatePath(ROUTES.optionChain, {
            exchange: exchangeToJSON(security?.exchange ?? 1),
            symbol: security?.code
        })
    }, [security])

    const handleOptionChainClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation()
            AnalyticsManager.logEvent('Navigation Button Clicked', {
                source: 'watchlist',
                buttonType: ViewType.OPTION_CHAIN
            })
            if (!security || expressionMode) return
            if (view === ViewType.OPTION_CHAIN) {
                window.open(getPathForOptionChain(), '_blank')
            }
        },
        [security, expressionMode, view, getPathForOptionChain]
    )

    const tooltipTitle = useMemo(() => {
        if (securityExpression) return securityExpression.name
        if (referenceSecurity) return referenceSecurity.displayName
        return security?.displayName
    }, [securityExpression, selectedSecurity, referenceSecurity])

    const subText = useMemo(() => {
        if (expressionMode && securityExpression) {
            return convertExpressionToDisplayExpression(securityExpression.expression, securityExpression.securities)
        }
        if (!expressionMode) {
            const sec = selectedSecurity ?? referenceSecurity ?? security
            if (sec?.exchange !== undefined) {
                return exchangeToJSON(sec.exchange)
            }
        }
        return undefined
    }, [securityExpression, selectedSecurity, referenceSecurity, security, expressionMode])

    const handleQuickTrade = useCallback(
        (
            transaction: TransactionType,
            orderType: OrderType = OrderType.NORMAL,
            brokerOrderType = Broker_OrderType.MARKET_ORDER,
            product: Product = Product.CNC,
            price: number | undefined = undefined
        ) => {
            if (!security) return

            let quantity = security.lotSize || 1
            let finalProduct = product

            const intraday = finalProduct === Product.INTRADAY

            if (!intraday) {
                if (security.categoryCode === SecurityCategory_CategoryCode.FUTURES || security.categoryCode === SecurityCategory_CategoryCode.OPTIONS) {
                    finalProduct = Product.MARGIN
                } else {
                    finalProduct = Product.CNC
                }
            }

            dispatch(
                setQuickOrderData({
                    openDialog: true,
                    mode: 'regular',
                    type: 'trade',
                    isSquareOffOrder: false,
                    order: {
                        transactionType: transaction,
                        security,
                        editOrder: undefined,
                        brokerOrderType: brokerOrderType,
                        orderType,
                        product: finalProduct,
                        price: price,
                        quantity,
                        maxQuantity: 0
                    }
                })
            )
        },
        [dispatch, security]
    )
    const title = useMemo(() => {
        return (
            <Grid container direction="column" sx={{ width: 'max-content', height: 'auto' }}>
                <>
                    <Grid item>
                        <Typography variant="body2">
                            {tooltipTitle}{!expressionMode && subText ? ` | ${subText}` : ''}
                        </Typography>
                    </Grid>
                    {expressionMode && subText && (
                        <Grid item>
                            <Typography variant="body2">{subText}</Typography>
                        </Grid>
                    )}
                </>
            </Grid>
        )
    }, [tooltipTitle, expressionMode, subText])

    const handleAddSection = useCallback(async () => {
        if (!selectedWatchlist) return

        const insertIndex = contextMenu?.index ?? 0

        const allSectionNames = new Set(
            Object.values(watchlists).flatMap(
                (wl) => wl.watchlistItems?.filter((item) => item.item.type === Watchlist_WatchlistItem_Type.SECTION).map((section) => section.item.sectionName?.trim()) || []
            )
        )
        let sectionIndex = 1
        let baseName = 'Section'
        let generatedName = `${baseName} ${sectionIndex}`
        while (allSectionNames.has(generatedName)) {
            sectionIndex++
            generatedName = `${baseName} ${sectionIndex}`
        }

        const prevItem = selectedWatchlist?.watchlistItems?.[insertIndex - 1]
        const nextItem = selectedWatchlist?.watchlistItems?.[insertIndex]
        const prevPriority = prevItem?.item?.priority ?? 0
        const nextPriority = nextItem?.item?.priority ?? prevPriority
        const newPriority = (prevPriority + nextPriority) / 2

        const newSection: Watchlist_WatchlistItem = {
            type: Watchlist_WatchlistItem_Type.SECTION,
            sectionName: generatedName,
            priority: newPriority
        }

        const newItem = {
            id: `${Date.now()}`,
            item: newSection
        }

        const updatedItems = [...(selectedWatchlist?.watchlistItems || [])]
        updatedItems.splice(insertIndex, 0, newItem)

        dispatch(
            setWatchlists({
                ...watchlists,
                [selectedWatchlist.id]: {
                    ...selectedWatchlist,
                    watchlistItems: updatedItems
                }
            })
        )

        try {
            await changeWatchlistSecurities({
                watchlistItem: newSection,
                watchlistId: selectedWatchlist.id,
                method: 'POST'
            })

            await updateWatchlist(dispatch, watchlistId, dataUpdating, setLoading, false, true, false)
        } catch (error) {
            console.error('Failed to add section or refresh watchlist:', error)
        }

        setContextMenu(null)
    }, [selectedWatchlist, contextMenu?.index, watchlists, dispatch, watchlistId])

    const addOrRemoveWatchlistItem = useCallback(
        async (watchlistItem: Watchlist_WatchlistItem, addToWatchlist: boolean, watchlistId: string) => {
            try {
                if (watchlistItem) {
                    await changeWatchlistSecurities({
                        watchlistItem: addToWatchlist ? watchlistItem : { watchlistItems: [watchlistItem] },
                        watchlistId: watchlistId,
                        method: addToWatchlist ? 'POST' : 'DELETE'
                    })
                    await updateWatchlist(dispatch, watchlistId, dataUpdating, setLoading, false, true, false)
                }
            } catch (err: any) {
                const { errorCode, message } = parseErrorFromArrayBuffer(err)
            }
        },
        [dispatch]
    )

    const onSecurityClicked = useCallback(
        async (data: ExpressionCallbackData, selected: boolean) => {
            if (data && data.securities && data.securities.length > 0) {
                const savedExpression = getExpressionFromSaved(savedExpressions, data.expression)
                const isExpressionMode = data.expression.length > 0 && ['STRATEGY_EXPRESSION', 'EXPRESSION'].includes(data.type ?? '')

                if (isExpressionMode) {
                    onSecuritySearchModalClose()
                    dispatch(
                        setSecuritySearchOpen({
                            open: false,
                            key: undefined,
                            isExpression: false
                        })
                    )
                }
                if (isExpressionMode && data.type === 'STRATEGY_EXPRESSION') {
                    commonActions.updateStrategyExpressionMetaData(dispatch, {
                        name: data.name ?? convertExpressionToDisplayExpression(data.expression, data.securities),
                        expression: data.expression
                    })
                }

                if (isExpressionMode && selected && data.type !== 'STRATEGY_EXPRESSION' && (savedExpression === undefined || savedExpression.name === undefined)) {
                    setExpressionNameCallbackData({
                        type: Watchlist_WatchlistItem_Type.EXPRESSION,
                        security: undefined,
                        securityExpression: {
                            id: data.expressionId ?? data.expression,
                            name: '',
                            expression: data.expression,
                            securities: data.securities,
                            expired: false
                        }
                    })
                } else {
                    if (isExpressionMode && data.type === 'STRATEGY_EXPRESSION') {
                        await addOrRemoveWatchlistDataItem(
                            {
                                type: Watchlist_WatchlistItem_Type.EXPRESSION,
                                security: undefined,
                                securityExpression: {
                                    id: data.expressionId ?? data.expression,
                                    expression: data.expression,
                                    name: data.name ?? '',
                                    securities: data.securities,
                                    expired: true
                                }
                            },
                            selected,
                            selectedWatchlistId
                        )
                        return
                    }

                    if (isExpressionMode && !savedExpression) {
                        commonActions.setApiError(dispatch, {
                            error: true,
                            message: 'Something went wrong',
                            errorType: 'error'
                        })
                        return
                    }
                    const prevItem = selectedWatchlist?.watchlistItems?.[index - 1]
                    const nextItem = selectedWatchlist?.watchlistItems?.[index]
                    const prevPriority = prevItem?.item?.priority ?? 0
                    const nextPriority = nextItem?.item?.priority ?? prevPriority
                    const newPriority = (prevPriority + nextPriority) / 2

                    const watchlistItem: Watchlist_WatchlistItem = isExpressionMode
                        ? {
                              type: Watchlist_WatchlistItem_Type.EXPRESSION,
                              security: undefined,
                              securityExpression: savedExpression,
                              priority: newPriority
                          }
                        : {
                              type: Watchlist_WatchlistItem_Type.SECURITY,
                              security: data.securities[0],
                              priority: newPriority,
                              securityExpression: undefined
                          }
                    await addOrRemoveWatchlistItem(watchlistItem, selected, watchlistId)
                }
            }
        },
        [savedExpressions, onSecuritySearchModalClose, dispatch, selectedWatchlist?.watchlistItems, index, addOrRemoveWatchlistItem, watchlistId, addOrRemoveWatchlistDataItem, selectedWatchlistId]
    )

    const actionButtons = useMemo(
        () => (
            <Stack
                spacing={0.5}
                direction="row"
                className="action-btns"
                sx={{
                    position: 'absolute',
                    right: 2,
                    zIndex: 10,
                    height: '28px',
                    visibility: 'hidden',
                    '&:hover': {
                        visibility: 'visible'
                    }
                }}
            >
                {security?.hasFo && (
                    <NonInteractiveTooltip
                        placement="top"
                        arrow={true}
                        title={
                            <Typography
                                variant={'caption'}
                                sx={{
                                    maxWidth: 'none',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {'Option Chain'}
                            </Typography>
                        }
                    >
                        <ExtraSmallButtonHoverable
                            variant="outlined"
                            color="inherit"
                            sx={{ maxWidth: '32px', padding: 0 }}
                            onClick={(e) => {
                                handleOptionChainClick(e)
                            }}
                        >
                            <OptionChain sx={{ fontSize: '28px' }} />
                        </ExtraSmallButtonHoverable>
                    </NonInteractiveTooltip>
                )}
                <NonInteractiveTooltip
                    placement="top"
                    arrow={true}
                    title={
                        <Typography
                            variant={'caption'}
                            sx={{
                                maxWidth: 'none',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {'Charts'}
                        </Typography>
                    }
                >
                    <ExtraSmallButtonHoverable
                        variant="outlined"
                        color="inherit"
                        sx={{ maxWidth: '32px', padding: 0 }}
                        onClick={(e) => {
                            e.stopPropagation()
                            if(!hasAccess(FeatureCategory.WATCHLIST, '', FeatureName.COMMUNITY_WATCHLIST_ACCESS)){
                                dispatch(setFeatureRestrictionDialog(
                                    {
                                        open: true,
                                        feature: "Security spreads"
                                    }
                                ))
                                return
                            }
                            if (securityExpression) {
                                handleOverviewSecurityClick(securityExpression?.expression)
                            } else {
                                handleOverviewSecurityClick(security)
                            }
                        }}
                    >
                        <Chart sx={{ fontSize: '28px' }} />
                    </ExtraSmallButtonHoverable>
                </NonInteractiveTooltip>

                {!expressionMode && security?.categoryCode !== SecurityCategory_CategoryCode.INDEX && (
                    <>
                        <NonInteractiveTooltip
                            placement="top"
                            arrow={true}
                            title={
                                <Typography
                                    variant={'caption'}
                                    sx={{
                                        maxWidth: 'none',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {'Buy'}
                                </Typography>
                            }
                        >
                            <ExtraSmallButtonHoverable
                                variant={'outlined'}
                                color={'inherit'}
                                sx={{
                                    width: '28px',
                                    height: '28px',
                                    padding: 0,
                                    backgroundColor: alpha(blue, 0.8),
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: blue
                                    },
                                    '&:active': {
                                        backgroundColor: blue
                                    }
                                }}
                                onClick={(clickEvent: React.MouseEvent<HTMLSpanElement>) => {
                                    clickEvent.stopPropagation()
                                    handleQuickTrade(TransactionType.BUY)
                                }}
                            >
                                B
                            </ExtraSmallButtonHoverable>
                        </NonInteractiveTooltip>
                        <NonInteractiveTooltip
                            placement="top"
                            arrow={true}
                            title={
                                <Typography
                                    variant={'caption'}
                                    sx={{
                                        maxWidth: 'none',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {'Sell'}
                                </Typography>
                            }
                        >
                            <ExtraSmallButtonHoverable
                                variant={'outlined'}
                                color={'inherit'}
                                sx={{
                                    width: '28px',
                                    height: '28px',
                                    padding: 0,
                                    background: alpha(red, 0.8),
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: red
                                    },
                                    '&:active': {
                                        backgroundColor: red
                                    }
                                }}
                                onClick={(clickEvent: React.MouseEvent<HTMLSpanElement>) => {
                                    clickEvent.stopPropagation()
                                    handleQuickTrade(TransactionType.SELL)
                                }}
                            >
                                S
                            </ExtraSmallButtonHoverable>
                        </NonInteractiveTooltip>
                    </>
                )}
                {!expressionMode && (
                    <NonInteractiveTooltip
                        placement="top"
                        arrow={true}
                        title={
                            <Typography
                                variant={'caption'}
                                sx={{
                                    maxWidth: 'none',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {'Market Depth'}
                            </Typography>
                        }
                    >
                        <ExtraSmallButtonHoverable
                            variant={'outlined'}
                            color={'inherit'}
                            sx={{
                                width: '28px',
                                height: '28px',
                                padding: 0
                            }}
                            onClick={(clickEvent: React.MouseEvent<HTMLSpanElement>) => {
                                clickEvent.stopPropagation()
                                handleExpandClick()
                            }}
                        >
                            <FormatAlignCenterIcon sx={{ fontSize: '14px' }} />
                        </ExtraSmallButtonHoverable>
                    </NonInteractiveTooltip>
                )}
                {shouldAllowAccess(selectedWatchlist, editMode) && shouldAllowAdminAccess(selectedWatchlist?.shareId) && (
                    <NonInteractiveTooltip
                        placement="top"
                        arrow={true}
                        title={
                            <Typography
                                variant={'caption'}
                                sx={{
                                    maxWidth: 'none',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {'Delete'}
                            </Typography>
                        }
                    >
                        <ExtraSmallButtonHoverable
                            variant={'outlined'}
                            color={'inherit'}
                            sx={{
                                width: '28px',
                                height: '28px',
                                padding: 0
                            }}
                            onClick={(clickEvent: React.MouseEvent<HTMLSpanElement>) => {
                                clickEvent.stopPropagation()
                                addOrRemoveWatchlistDataItem([data], false, watchlistId)
                            }}
                        >
                            <BinIcon fontSize={'small'} />
                        </ExtraSmallButtonHoverable>
                    </NonInteractiveTooltip>
                )}
            </Stack>
        ),
        [security, handleOptionChainClick, expressionMode, handleQuickTrade, handleExpandClick, addOrRemoveWatchlistDataItem, data, watchlistId]
    )

    const item = useMemo(() => {
        return (
            <>
                <MuiTableRow sx={{ height: '1px', boxSizing: 'border-box' }}>
                    {isDraggedOver ? (
                        <TableCell colSpan={numberOfTableColumns} padding={'none'} sx={{ maxHeight: '1px', borderBottom: 'none' }}>
                            <div style={{ background: primaryColor, height: '1px' }}></div>
                        </TableCell>
                    ) : null}
                </MuiTableRow>
                <NonInteractiveTooltip placement="left" title={title} disableInteractive={false} arrow enterDelay={700} enterNextDelay={0}>
                <MuiTableRow
                    ref={itemRef}
                    component="tr"
                    tabIndex={0}
                    data-watchlist-focus-id={data.id}
                    onDragEnter={(e) => {
                        if (!shouldAllowAccess(selectedWatchlist, editMode) && !shouldAllowAdminAccess(selectedWatchlist?.shareId)) return
                        rectCache.current = e.currentTarget.getBoundingClientRect()
                    }}
                    onDragStart={(e) => {
                        if (!shouldAllowAccess(selectedWatchlist, editMode) && !shouldAllowAdminAccess(selectedWatchlist?.shareId)) return
                        handleDragStart(e, data)
                    }}
                    onDragOver={(e) => {
                        if (!shouldAllowAccess(selectedWatchlist, editMode) && !shouldAllowAdminAccess(selectedWatchlist?.shareId)) return
                        if (!rectCache) return
                        const centerY = (rectCache.current?.top ?? 0) + (rectCache.current?.height ?? 0) / 2
                        if (e.clientY < centerY) {
                            handleDragOver(e, index)
                        } else {
                            handleDragOver(e, index + 1)
                        }
                        e.preventDefault()
                    }}
                    onDragEnd={handleDragEnd as any}
                    draggable={!expanded}
                    onMouseDown={(e) => {
                        if (e.button !== 0) return
                        if (e.metaKey || e.ctrlKey) return
                        if ((e.target as HTMLElement).closest('button, a, [role="button"]')) return
                        activateItem()
                    }}
                    onClick={(e) => {
                        if (e.metaKey || e.ctrlKey) {
                            onItemClick(e, data as any)
                        }
                    }}
                    onContextMenu={(e) => {
                        e.preventDefault()
                        setContextMenu(contextMenu === null ? { mouseX: e.clientX - 2, mouseY: e.clientY - 4, index } : null)
                    }}
                    id={`watchlist-item-${index}`}
                    onFocus={(e) => {
                        if (e.nativeEvent instanceof FocusEvent && e.nativeEvent.detail === 0) return
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Delete' || e.key === 'Backspace') {
                            e.preventDefault()
                            onClearFocus?.()
                            addOrRemoveWatchlistDataItem(data, false, watchlistId)
                        }
                    }}
                   
                    sx={{
                        ...(isHighlighted || selectedItemIds.has(data.id)
                            ? {
                                  backgroundColor: '#E3F2FD'
                              }
                            : {}),
                        cursor: 'default',
                        ...(isDraggedOver
                            ? {
                                  borderTop: 2,
                                  borderTopColor: primaryColor
                              }
                            : {}),
                        '&:hover .action-btns': {
                            visibility: 'visible'
                        },
                        ':hover': {
                            backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#252525' : '#F5F5F5')
                        },
                        boxSizing: 'border-box',
                        backgroundColor:
                            isRightClicked && shouldAllowAccess(selectedWatchlist, editMode) && shouldAllowAdminAccess(selectedWatchlist?.shareId) ? alpha(primaryColor, 0.1) : 'transparent',
                        '&.Mui-selected': {
                            backgroundColor: 'transparent'
                        },
                        '&.Mui-selected:hover': {
                            backgroundColor: 'transparent'
                        },
                        '&:focus': {
                            background: 'transparent !important'
                        },
                        '&[data-keyboard-focused="true"] td': {
                            borderTop: '1px solid #1890ff !important',
                            borderBottom: '1px solid #1890ff !important'
                        },
                        '&[data-keyboard-focused="true"] td:first-of-type': {
                            borderLeft: '1px solid #1890ff !important'
                        },
                        '&[data-keyboard-focused="true"] td:last-of-type': {
                            borderRight: '1px solid #1890ff !important'
                        },
                        ...(isMatched && matchPath(ROUTES.chart, window.location.pathname) && {
                            '.watchlist-data-root:not([data-keyboard-nav]):not([data-watchlist-item-focused]) & td': {
                                borderTop: '1px solid #1890ff',
                                borderBottom: '1px solid #1890ff'
                            },
                            '.watchlist-data-root:not([data-keyboard-nav]):not([data-watchlist-item-focused]) & td:first-of-type': {
                                borderLeft: '1px solid #1890ff'
                            },
                            '.watchlist-data-root:not([data-keyboard-nav]):not([data-watchlist-item-focused]) & td:last-of-type': {
                                borderRight: '1px solid #1890ff'
                            }
                        }),
                        '& td': {
                            padding: 0,
                            borderTop: `0px solid transparent`,
                            borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                        },
                        '& td:first-of-type': {
                            borderLeft: '1px solid transparent'
                        },
                        [`& td:last-of-type`]: {
                            borderRight: '1px solid transparent'
                        },
                        height: '34px ! important',
                        display: 'table-row',
                        tableLayout: 'fixed',
                        position: 'relative'
                    }}
                >
                    <TableCell
                        sx={{
                            height: '34px',
                            boxSizing: 'border-box',
                            padding: '0px !important',
                            fontSize: 13,
                            overflow: 'visible'
                        }}
                    >
                        <Box
                            className="table-cell"
                            paddingLeft={'10px'}
                            sx={{ minWidth: 'max-content' }}
                        >
                            <SymbolDisplay
                                securityList={securityExpression ? securityExpression.securities : security ? [security] : []}
                                expression={securityExpression?.expression || ''}
                                expressionMode={expressionMode}
                                expressionName={securityExpression?.name ?? ''}
                                fontSize={12}
                                logoSize={20}
                                onPopoverOpenChange={setPopoverOpen}
                            />
                        </Box>
                    </TableCell>
                    <WatchlistTableItemPrices item={data.item} security={security} />
                    <TableCell
                        sx={{
                            position: 'sticky',
                            right: 0,
                            padding: 0,
                            width: 0,
                            border: 'none'
                        }}
                    >
                        <Box
                            className="action-btns"
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                visibility: 'hidden'
                            }}
                        >
                            {actionButtons}
                        </Box>
                    </TableCell>
                </MuiTableRow>
                </NonInteractiveTooltip>
            </>
        )
    }, [
        isDraggedOver,
        numberOfTableColumns,
        handleDragEnd,
        expanded,
        index,
        isHighlighted,
        selectedItemIds,
        data,
        isMatched,
        isRightClicked,
        securityExpression,
        security,
        expressionMode,
        actionButtons,
        handleDragStart,
        handleDragOver,
        onItemClick,
        onClearFocus,
        activateItem,
        contextMenu,
        addOrRemoveWatchlistDataItem,
        watchlistId
    ])
    return (
        <>
            {item}
            {expanded && !expressionMode && security ? (
                <MuiTableRow>
                    <TableCell colSpan={5} sx={{ p: 0, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ width: '100%' }}>
                            <OrderBookDialog security={security} />
                            <Box px={2} py={1}>
                                <SecurityRange security={security} />
                            </Box>
                        </Box>
                    </TableCell>
                </MuiTableRow>
            ) : null}
            {selectedWatchlist && shouldAllowAccess(selectedWatchlist, editMode) && shouldAllowAdminAccess(selectedWatchlist?.shareId) && (
                <Menu
                    open={contextMenu !== null}
                    onClose={() => setContextMenu(null)}
                    anchorReference="anchorPosition"
                    anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                    componentsProps={{
                        root: {
                            onContextMenu: (e: React.MouseEvent) => {
                                e.preventDefault()
                            }
                        }
                    }}
                >
                    <MenuItem onClick={handleAddSection}>
                        {' '}
                        <IconButton sx={{ padding: '0px', mr: '10px' }}>
                            <AddSectionIcon />
                        </IconButton>
                        Add Section
                    </MenuItem>
                    <MenuItem onClick={onAddSymbolSectionOrEnd}>
                        <IconButton sx={{ padding: '0px', mr: '10px' }}>
                            <PlusIcon />
                        </IconButton>
                        Add Symbol
                    </MenuItem>
                    {modalOpen && (
                        <SecuritySearchModal
                            open={modalOpen}
                            enableExpressions={true}
                            onCloseClicked={onSecuritySearchModalClose}
                            onSecuritySelected={onSecurityClicked}
                            selectedItems={selectedItems}
                            tailIcon
                            watchlistMode
                        />
                    )}
                </Menu>
            )}
        </>
    )
}
export default memo(WatchListTableView, (prev, next) =>
    prev.data === next.data &&
    prev.isDraggedOver === next.isDraggedOver &&
    prev.selectedItemIds === next.selectedItemIds &&
    prev.editMode === next.editMode &&
    prev.showBottomDragLine === next.showBottomDragLine
)
